import React from "react";

import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Constants from "./../../../../Utils/Constants";
import axios from "axios";
import { getHeaders, getToken } from "./../../../../Utils/Common";
import store from "./../../../../Common/storage";
import { calculateSmsUsage, shortenUrlsForPreview } from "./../../../../Utils/smsUsage";
import { renderMessagePreview } from "./../../../../Utils/messagePreview";
import validator from "validator";
import LoadingProgress from "./../../../../Common/LoadingProgress";
import toastMaker from "./../../../../Common/toastmaker";

function SimpleDialog(props) {
  const { templatetext, senderId, subject, url, file, isMMS, isShortLink } = props;

  const { onClose, open } = props;

  // Step machine: email -> searching -> not_found | no_phone | ready -> sending -> result
  const [step, setStep] = React.useState("email");
  const [email, setEmail] = React.useState("");
  const [contact, setContact] = React.useState(null);
  const [resultStatus, setResultStatus] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setStep("email");
      setEmail("");
      setContact(null);
      setResultStatus(null);
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const resetSearch = () => {
    setEmail("");
    setContact(null);
    setResultStatus(null);
    setStep("email");
  };

  const handleSearch = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || trimmedEmail.length === 0) {
      toastMaker.makeToast("Please enter an email address.", {
        variant: "error",
      });
      return;
    }
    if (!validator.isEmail(trimmedEmail)) {
      toastMaker.makeToast("Enter a valid email address.", {
        variant: "error",
      });
      return;
    }

    setStep("searching");

    try {
      const apiurl = Constants.getApiPathWithConfigId(Constants.searchACContactByEmailApiPath);
      const response = await axios.get(apiurl, {
        ...getHeaders(),
        params: { email: trimmedEmail },
      });
      const data = response?.data?.data;
      const found = Array.isArray(data) ? data[0] : data;

      if (!found || (!found.email && !found.mobile && !found.id)) {
        setStep("not_found");
        return;
      }

      const normalizedContact = {
        fname: found.fname || found.firstName || "",
        lname: found.lname || found.lastName || "",
        email: found.email || trimmedEmail,
        mobile: found.mobile || found.phone || "",
        id: found.id,
      };
      setContact(normalizedContact);
      setStep(normalizedContact.mobile.trim().length === 0 ? "no_phone" : "ready");
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 404) {
        setStep("not_found");
      } else {
        toastMaker.makeToast(
          error?.response?.data?.message || "Something went wrong while searching for the contact.",
          { variant: "error" }
        );
        setStep("email");
      }
    }
  };

  const handleSend = async () => {
    setStep("sending");

    const apiurl = isMMS
      ? Constants.getApiPathWithConfigId(Constants.sendTestMMSApiPath)
      : Constants.getApiPathWithConfigId(Constants.sendTestSMSApiPath);

    const formData = new FormData();
    formData.append("to", [contact.mobile]);
    formData.append("message", renderMessagePreview(templatetext, contact));
    formData.append("from", senderId);
    formData.append("type", "test");
    formData.append("is_short_link_on", isShortLink ? 1 : 0);

    if (isMMS) {
      formData.append("file", file[0]);
      formData.append("subject", subject);
      formData.append("url", url);
    }

    try {
      const response = await axios.post(apiurl, formData, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setResultStatus({
        success: true,
        message: `${isMMS ? "MMS" : "SMS"} sent successfully to ${contactName}${contact.mobile ? ` (${contact.mobile})` : ""}.`,
      });
    } catch (error) {
      console.error(error);
      setResultStatus({
        success: false,
        message:
          error?.response?.data?.message ||
          `Failed to send ${isMMS ? "MMS" : "SMS"}. Please try again.`,
      });
    }

    setStep("result");
  };

  const contactName = [contact?.fname, contact?.lname].filter(Boolean).join(" ") || "—";

  const smsUsage = templatetext ? calculateSmsUsage(templatetext, isMMS, isShortLink) : null;
  const creditsRequired = smsUsage?.credits ?? 1;
  const availableCredits = localStorage.getItem("creds") ?? 0;
  const isPostpaid = store.get("user")?.configs?.[store.get("c_id_index")]?.allow_exceed;
  const hasEnoughCredits = availableCredits >= creditsRequired;

  if (step === "searching" || step === "sending") {
    return <LoadingProgress />;
  }

  let dialogContent;

  if (step === "email") {
    dialogContent = (
      <div>
        <DialogTitle id="simple-dialog-title">
          Enter contact email to send {isMMS ? "MMS" : "SMS"}
        </DialogTitle>
        <DialogContent>
          <div className="form-groups">
            <input
              id="testContactEmail"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleSearch();
              }}
              type="email"
              placeholder="contact@example.com"
              className="form-controls"
            />
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 24px" }}>
          <span
            className="c-button text-uppercase"
            style={{ marginRight: "10px" }}
            onClick={handleClose}
          >
            Cancel
          </span>
          <span
            className="c-button c-button--secondary text-uppercase"
            onClick={handleSearch}
          >
            Search Contact
          </span>
        </DialogActions>
      </div>
    );
  } else if (step === "not_found") {
    dialogContent = (
      <div>
        <DialogTitle id="simple-dialog-title">Contact not found</DialogTitle>
        <DialogContent>
          <p>
            No ActiveCampaign contact was found for <b>{email}</b>.
          </p>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 24px" }}>
          <span
            className="c-button text-uppercase"
            style={{ marginRight: "10px" }}
            onClick={handleClose}
          >
            Cancel
          </span>
          <span
            className="c-button c-button--secondary text-uppercase"
            onClick={resetSearch}
          >
            Try Another Email
          </span>
        </DialogActions>
      </div>
    );
  } else if (step === "no_phone") {
    dialogContent = (
      <div>
        <DialogTitle id="simple-dialog-title">No phone number on file</DialogTitle>
        <DialogContent>
          <p>
            <b>{contactName}</b> ({contact?.email})
          </p>
          <p style={{ color: "#d32f2f" }}>
            This contact does not have a phone number on file in ActiveCampaign, so an{" "}
            {isMMS ? "MMS" : "SMS"} can't be sent to them.
          </p>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 24px" }}>
          <span
            className="c-button text-uppercase"
            style={{ marginRight: "10px" }}
            onClick={handleClose}
          >
            Cancel
          </span>
          <span
            className="c-button c-button--secondary text-uppercase"
            onClick={resetSearch}
          >
            Search Another Contact
          </span>
        </DialogActions>
      </div>
    );
  } else if (step === "ready") {
    const sendDisabled = !hasEnoughCredits && !isPostpaid;
    dialogContent = (
      <div>
        <DialogTitle id="simple-dialog-title">
          Send {isMMS ? "MMS" : "SMS"} to contact
        </DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 12 }}>
            <div>
              <b>Name:</b> {contactName}
            </div>
            <div>
              <b>Email:</b> {contact.email}
            </div>
            <div>
              <b>Phone:</b> {contact.mobile}
            </div>
            {isMMS && subject && subject.length > 0 && (
              <div style={{ marginTop: "6px" }}>
                <b>Subject:</b> {subject}
              </div>
            )}
            {isMMS && url && url.length > 0 && (
              <div style={{ marginTop: "6px" }}>
                <img src={url} width={200} alt="MMS attachment" />
              </div>
            )}
            <div style={{ whiteSpace: "pre-wrap", marginTop: "6px" }}>
              <b>Message:</b> {shortenUrlsForPreview(renderMessagePreview(templatetext, contact), isShortLink)}
            </div>
          </div>
          <div style={{ margin: "12px 0" }}>
            <div style={{ fontWeight: 600, fontSize: "medium", marginBottom: "4px" }}>
              Credits required:{" "}
              <span style={{ color: hasEnoughCredits ? "inherit" : "#d32f2f" }}>
                {creditsRequired}
              </span>
            </div>
            {!isPostpaid && (
              <div style={{ fontWeight: 600, fontSize: "medium", marginBottom: "4px" }}>
                Available credits: {availableCredits}
              </div>
            )}
            {sendDisabled && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "10px 14px",
                  backgroundColor: "#fff3e0",
                  border: "1px solid #ff9800",
                  borderRadius: "6px",
                  color: "#e65100",
                  fontSize: "14px",
                }}
              >
                ⚠️ You do not have enough credits to send this {isMMS ? "MMS" : "SMS"}.
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions
          style={{
            padding: "8px 24px 24px",
            flexWrap: "nowrap",
            gap: "8px",
            justifyContent: "flex-start",
          }}
        >
          <span className="c-button text-uppercase" onClick={handleClose}>
            Cancel
          </span>
          <span className="c-button text-uppercase" onClick={resetSearch}>
            Search Another Contact
          </span>
          <span
            className="c-button c-button--secondary text-uppercase"
            style={sendDisabled ? { opacity: 0.5, pointerEvents: "none" } : {}}
            onClick={handleSend}
          >
            Send {isMMS ? "MMS" : "SMS"}
          </span>
        </DialogActions>
      </div>
    );
  } else if (step === "result") {
    const success = resultStatus?.success;
    dialogContent = (
      <div>
        <DialogTitle id="simple-dialog-title">
          {success ? "Message sent" : "Send failed"}
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              padding: "10px 14px",
              borderRadius: "6px",
              fontSize: "14px",
              backgroundColor: success ? "#e8f5e9" : "#fdecea",
              border: `1px solid ${success ? "#4caf50" : "#f44336"}`,
              color: success ? "#2e7d32" : "#c62828",
            }}
          >
            {success ? "✅" : "❌"} {resultStatus?.message}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 24px" }}>
          <span
            className="c-button text-uppercase"
            style={{ marginRight: "10px" }}
            onClick={handleClose}
          >
            Close
          </span>
          <span
            className="c-button c-button--secondary text-uppercase"
            onClick={resetSearch}
          >
            Send Another Message
          </span>
        </DialogActions>
      </div>
    );
  }

  return (
    <>
      <Dialog
        onClose={handleClose}
        aria-labelledby="simple-dialog-title"
        open={open}
      >
        {dialogContent}
      </Dialog>
    </>
  );
}

export default function SimpleDialogDemo({ templatetext, senderId, subject, url, file, isMMS, isShortLink }) {
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    if (
      senderId &&
      senderId.length > 0 &&
      templatetext &&
      templatetext.length > 0
    ) {
      setOpen(true);
    } else {
      toastMaker.makeToast(
        "Please ensure SenderId and Template Text are filled",
        {
          variant: "error",
        }
      );
      return;
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <React.Fragment>
      <span
        className="c-button c-button--secondary text-uppercase"
        onClick={handleClickOpen}
      >
        Send to Contact
      </span>
      <SimpleDialog
        open={open}
        onClose={handleClose}
        templatetext={templatetext}
        senderId={senderId}
        subject={subject}
        url={url}
        file={file}
        isMMS={isMMS}
        isShortLink={isShortLink}
      />
    </React.Fragment>
  );
}
